import { selectApiData } from 'util/selector/apiSelector';
import { selectResponseData } from 'util/selector/axiosSelector';

import { LangCodeEnum } from 'shared/enums/LangCodeEnum';
import { AxiosInstance } from 'axios';
import { PulseLifecycleProjectReceivedEntry } from 'model/Lifecycle';
import { SortDirection } from 'shared/constants';
import { PaginationDto } from 'model/Pagination.dto';

import { ClassificationDateDto } from './dto/ClassificationDate.dto';
import { ClassificationDto } from './dto/Classification.dto';
import { EmployeeStatsModel } from './dto/EmploeeStats.dto';
import { DepartmentDto } from './dto/Department.dto';

export namespace EmployeeApi {
  export type Entry = {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    customAccessCode?: string;
    preferredLanguage?: LangCodeEnum | null;
    department: DepartmentDto | number;
    departmentNestedName?: string;
    demographyClassifications?: ClassificationDto | number[];
    classificationValues?: ClassificationDateDto[];
    active: number;
    createdAt?: string;
    invitedAt?: string;
    lifecycleSurveys?: PulseLifecycleProjectReceivedEntry[];
  };
  export type PaginationEntry = PaginationDto & {
    _embedded: {
      items: EmployeeApi.Entry[];
    };
  };
  export type FilterActiveParam = 0 | 1;

  export type SortParam = {
    name?: SortDirection;
    surveyMethod?: SortDirection;
    createdAt?: SortDirection;
    demography?: {
      [demographyId: number]: SortDirection;
    };
  };
}

export class EmployeeApi {
  constructor(private client: AxiosInstance) {}

  list(projectId: number, query: string): Promise<EmployeeApi.PaginationEntry> {
    return this.client
      .get(`/api/v1/project/${projectId}/respondent${query}`)
      .then((response) => selectResponseData(response));
  }

  async process(projectId: number, file: File): Promise<void> {
    const data = new FormData();
    data.append('document', file);

    await this.client.post(`/api/v1/project/${projectId}/employees-excel/process`, data);
  }

  async commit(projectId: number, jobId: number, query: string): Promise<void> {
    await this.client.post(`/api/v1/project/${projectId}/employees-excel/${jobId}/commit${query}`);
  }

  async add(projectId: number, employee: EmployeeApi.Entry): Promise<void> {
    await this.client.post(`api/v1/project/${projectId}/employee`, employee);
  }

  edit(
    projectId: number,
    employeeId: number,
    employee: EmployeeApi.Entry,
  ): Promise<EmployeeApi.Entry> {
    return this.client.post(`/api/v1/employee/${employeeId}`, { ...employee }).then(selectApiData);
  }

  async delete(employeeId: number): Promise<void> {
    await this.client.delete(`api/v1/employee/${employeeId}`);
  }

  stats(projectId: number): Promise<EmployeeStatsModel> {
    return this.client
      .get(`/api/v1/project/${projectId}/employees-excel/stats`)
      .then((response) => response.data);
  }

  updateEmail(employeeId: number, email: string): Promise<void> {
    return this.client.post(`api/v1/employee-email/${employeeId}`, { email });
  }
}
